import { Component, HostListener, Input, OnChanges } from "@angular/core";
import { Meeting } from "@common/ADAPT.Common.Model/organisation/meeting";
import { MeetingAgendaItem } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-item";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { DateUtilities } from "@common/lib/utilities/date-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { MeetingsService } from "@org-common/lib/meetings/meetings.service";
import moment from "moment";
import { filter, interval, merge, Subject } from "rxjs";

enum MeetingStatusStyleClass {
    OnTime = "on-time",
    Slow = "slow",
    Overdue = "overdue",
}

@Component({
    selector: "adapt-meeting-progress-indicator",
    standalone: false,
    templateUrl: "./meeting-progress-indicator.component.html",
    styleUrl: "./meeting-progress-indicator.component.scss",
})
export class MeetingProgressIndicatorComponent extends BaseComponent implements OnChanges {
    public readonly MeetingStatusText = {
        [MeetingStatusStyleClass.OnTime]: "On time",
        [MeetingStatusStyleClass.Slow]: "Behind schedule",
        [MeetingStatusStyleClass.Overdue]: "Meeting overtime",
    };

    @Input() public meeting!: Meeting;
    @Input() public radius = 18;
    @Input() public singleRow = false; // will only use single row if showing on toolbar when there is enough space

    // when toggle between page and sidebar, popover tip will be wrong without this
    // Current angle for progress indicator
    public angle = 0;
    public statusFillClass = MeetingStatusStyleClass.OnTime;
    public showElapsedTime = true; // either showing elapsed time or expected end time
    public elapsedTimeText = "";
    public estimateEndTime?: Date;

    private triggerUpdate$ = new Subject<void>();

    public constructor(
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super();

        merge(
            interval(60000), // update every minute
            this.triggerUpdate$
        ).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => {
            if (this.meeting) {
                this.calculateAngle();
            }
        });

        // also trigger update if meeting time or agenda items change
        merge(
            rxjsBreezeService.entityTypeChanged(Meeting),
            rxjsBreezeService.entityTypeChanged(MeetingAgendaItem),
        ).pipe(
            filter((changedEntity) => this.meeting && changedEntity.meetingId === this.meeting.meetingId),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.triggerUpdate$.next());
    }

    @HostListener("click")
    public handleClick(): void {
        this.showElapsedTime = !this.showElapsedTime;
    }

    public ngOnChanges() {
        if (this.meeting) {
            this.calculateAngle();
            this.isInitialised = true;
        }
    }

    private calculateAngle() {
        const start = moment(this.meeting.meetingDateTime);
        const end = moment(this.meeting.endTime);
        const durationInMinutes = end.diff(start, "minutes");
        const now = moment();
        const elapsedTimeInMinutes = Math.max(0, now.diff(start, "minutes"));
        // Calculate the angle based on elapsed time and total duration
        this.angle = (elapsedTimeInMinutes / durationInMinutes) * 360;
        if (this.angle > 359) {
            this.angle = 359; // Cap the angle at 360 degrees (0 - 359)
        }

        const agendaItems = this.meeting.meetingAgendaItems ?? [];
        const remainingAgendaTime = MeetingsService.getMeetingAgendaItemsTotalPlannedDurationInMinutes(agendaItems.filter((i) => !i.extensions?.isEnded));
        if (elapsedTimeInMinutes >= durationInMinutes) {
            this.statusFillClass = MeetingStatusStyleClass.Overdue;
        } else {
            const currentItemDuration = agendaItems.find((i) => i.extensions.isInProgress && i.extensions.isAgendaItem)?.plannedDurationInMinutes ?? 0;
            // on-time or slow based on remaining agenda time vs remaining time, not including the current item
            this.statusFillClass = (remainingAgendaTime - currentItemDuration) >= (durationInMinutes - elapsedTimeInMinutes)
                ? MeetingStatusStyleClass.Slow
                : MeetingStatusStyleClass.OnTime;
        }

        this.elapsedTimeText = DateUtilities.minuteToDurationString(elapsedTimeInMinutes);
        // Estimate end time based on remaining agenda time (which will include current item as current item is not yet ticked off)
        // - see CM-5918 description
        // - Would be weird if current item is not included
        //  (e.g. out of 60min meeting, and current item is 30min, then it will show 30min remaining on startup, but actually it is 60min remaining)
        this.estimateEndTime = now.add(remainingAgendaTime, "minutes").toDate();
    }

    /**
     * Creates an SVG path description for a pie slice
     * @param endAngle - Ending angle in degrees
     * @returns SVG path description string
     */
    public describePieSlice(endAngle: number): string {
        const x = this.radius; // Center x coordinate
        const y = this.radius; // Center y coordinate
        const startAngle = 0;
        // Convert angles from degrees to radians (adjusting for 12 o'clock start)
        const startAngleRad = (startAngle - 90) * Math.PI / 180;
        const endAngleRad = (endAngle - 90) * Math.PI / 180;

        // Calculate start and end points
        const startX = x + this.radius * Math.cos(startAngleRad);
        const startY = y + this.radius * Math.sin(startAngleRad);
        const endX = x + this.radius * Math.cos(endAngleRad);
        const endY = y + this.radius * Math.sin(endAngleRad);

        // Determine arc flags
        const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

        // Build SVG path
        return [
            "M", x, y,              // Move to center
            "L", startX, startY,    // Line to arc start
            "A", this.radius, this.radius, 0, largeArcFlag, 1, endX, endY,  // Draw arc
            "Z",                     // Close path to center
        ].join(" ");
    }
}
